import {
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";

export interface UseLongPressOptions<T = HTMLElement> {
  /** Callback fired when long press is detected (>= 500ms) */
  onLongPress?: (element: T) => void;
  /** Callback fired on short press (< 500ms) */
  onClick?: (event: MouseEvent<T>) => void;
  /** Duration in milliseconds before long press is detected (default: 500) */
  delay?: number;
}

export interface UseLongPressHandlers<T = HTMLElement> {
  onMouseDown: (event: MouseEvent<T>) => void;
  onMouseUp: (event: MouseEvent<T>) => void;
  onMouseLeave: () => void;
  onTouchStart: (event: TouchEvent<T>) => void;
  onTouchEnd: (event: TouchEvent<T>) => void;
}

/**
 * Custom hook for handling long press gestures
 *
 * @param options Configuration options
 * @param options.onLongPress Callback fired when long press is detected
 * @param options.onClick Callback fired on regular click (when long press not detected)
 * @param options.delay Duration in ms before long press is detected (default: 500)
 *
 * @returns Event handlers to spread onto the target element
 *
 * @example
 * ```tsx
 * const handlers = useLongPress({
 *   onLongPress: (element) => console.log('Long pressed!', element),
 *   onClick: (e) => console.log('Clicked!', e),
 * });
 *
 * return <button {...handlers}>Press me</button>;
 * ```
 */
export function useLongPress<T extends HTMLElement = HTMLElement>({
  onLongPress,
  onClick,
  delay = 500,
}: UseLongPressOptions<T> = {}): UseLongPressHandlers<T> {
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef(false);
  const elementRef = useRef<T | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleLongPress = useCallback(() => {
    longPressTriggeredRef.current = true;
    if (elementRef.current && onLongPress) {
      onLongPress(elementRef.current);
    }
  }, [onLongPress]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (event: MouseEvent<T>) => {
      elementRef.current = event.currentTarget;
      longPressTriggeredRef.current = false;
      longPressTimerRef.current = window.setTimeout(() => {
        handleLongPress();
      }, delay);
    },
    [handleLongPress, delay],
  );

  const handleMouseUp = useCallback(
    (event: MouseEvent<T>) => {
      clearLongPressTimer();
      // Only trigger onClick if long press wasn't triggered
      if (!longPressTriggeredRef.current && onClick) {
        // Prevent default navigation if we have a custom onClick
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }
    },
    [clearLongPressTimer, onClick],
  );

  const handleMouseLeave = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (event: TouchEvent<T>) => {
      elementRef.current = event.currentTarget;
      longPressTriggeredRef.current = false;
      longPressTimerRef.current = window.setTimeout(() => {
        handleLongPress();
      }, delay);
    },
    [handleLongPress, delay],
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent<T>) => {
      clearLongPressTimer();
      // Only trigger onClick if long press wasn't triggered
      if (!longPressTriggeredRef.current && onClick) {
        // Prevent default navigation if we have a custom onClick
        event.preventDefault();
        event.stopPropagation();
        // Cast touch event to mouse event for onClick compatibility
        onClick(event as unknown as MouseEvent<T>);
      }
    },
    [clearLongPressTimer, onClick],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  return {
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  };
}
